import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import type { AtcManifestServiceApi } from "@/app/services/atc/AtcManifestService";
import { getAtcManifestService } from "@/app/services/atc/AtcManifestService";
import { AtcStation, AtcScenario, getStationLabelWithoutType } from "@/types/AtcManifest";
import { Logger } from "@/app/utils/logger";
import { getPerformanceTimingService, PERFORMANCE_MARKS } from "@/app/services/performance/PerformanceTimingService";
import { isCleanStationFetchEnabled } from "@/app/config/featureFlags";
import {
  cleanFetchStations,
  cleanSelectStation,
  cleanRefreshStationScenarios,
  loadStationScenariosWithCleanArchitecture,
} from "./cleanStationThunks";
import { ErrorHandlerService } from "@/app/services/error/ErrorHandlerService";

const LOG_TAG = 'atc-slice';
export const NO_ATC_FILES_MESSAGE_PREFIX = 'No ATC files are currently available';

interface AtcThunkExtra {
  atcManifestService?: AtcManifestServiceApi;
}

const resolveAtcManifestService = (extra: unknown): AtcManifestServiceApi => {
  const typedExtra = extra as AtcThunkExtra | undefined;
  return typedExtra?.atcManifestService ?? getAtcManifestService();
};

const normalizeStationKey = (value: string) => value.trim().toLowerCase();

const isSameStation = (left: AtcStation, right: AtcStation) =>
  normalizeStationKey(left.airportCode) === normalizeStationKey(right.airportCode) &&
  normalizeStationKey(left.stationType) === normalizeStationKey(right.stationType);

const findMatchingStation = (
  stations: AtcStation[],
  target: AtcStation | null
): AtcStation | null => {
  if (!target) {
    return null;
  }

  return stations.find((station) => isSameStation(station, target)) ?? null;
};

const mergeStationScenarios = (station: AtcStation, previous: AtcStation | null): AtcStation => ({
  ...station,
  scenarios: previous?.scenarios ?? null,
});

const hasAvailableScenarios = (
  station: AtcStation | null | undefined
): station is AtcStation & { scenarios: AtcScenario[] } =>
  Array.isArray(station?.scenarios) && station.scenarios.length > 0;

const buildNoAtcFilesMessage = (station: AtcStation) =>
  `${NO_ATC_FILES_MESSAGE_PREFIX} for ${station.displayName}.`;

const buildNoFallbackStationMessage = (station: AtcStation) =>
  `${NO_ATC_FILES_MESSAGE_PREFIX} for ${station.displayName}, and no alternative station could be loaded.`;

type StationLoader = (station: AtcStation, forceRefresh: boolean) => Promise<AtcStation>;

const resetCurrentScenarioState = (state: AtcState) => {
  state.currentScenario = null;
  state.currentScenarioIndex = -1;
  state.progress = 0;
  state.currentTime = 0;
  state.duration = 0;
};

const applyLoadedStationSelection = (state: AtcState, station: AtcStation) => {
  state.selectedStation = station;

  if (hasAvailableScenarios(station)) {
    const randomIndex = Math.floor(Math.random() * station.scenarios.length);
    state.currentScenarioIndex = randomIndex;
    state.currentScenario = station.scenarios[randomIndex];
    state.selectedStationError = null;
    state.progress = 0;
    state.currentTime = 0;
    state.duration = 0;
    state.isLoading = true;
    return;
  }

  resetCurrentScenarioState(state);
  state.selectedStationError = buildNoAtcFilesMessage(station);
  state.isLoading = false;
};

const applyRefreshedStationSelection = (state: AtcState, station: AtcStation) => {
  const previousScenarioId = state.currentScenario?.scenario_id ?? null;
  state.selectedStation = station;

  if (hasAvailableScenarios(station)) {
    const scenarios = station.scenarios;
    const pool =
      previousScenarioId && scenarios.length > 1
        ? scenarios.filter((scenario) => scenario.scenario_id !== previousScenarioId)
        : scenarios;

    const pickedScenario = pool[Math.floor(Math.random() * pool.length)];
    state.currentScenarioIndex = scenarios.findIndex(
      (scenario) => scenario.scenario_id === pickedScenario.scenario_id
    );
    state.currentScenario = pickedScenario;
    state.selectedStationError = null;
    state.progress = 0;
    state.currentTime = 0;
    state.duration = 0;
    state.isLoading = true;
    return;
  }

  resetCurrentScenarioState(state);
  state.selectedStationError = buildNoAtcFilesMessage(station);
  state.isLoading = false;
};

const loadStationWithFallback = async ({
  requestedStation,
  availableStations,
  forceRefresh,
  loadStation,
}: {
  requestedStation: AtcStation;
  availableStations: AtcStation[];
  forceRefresh: boolean;
  loadStation: StationLoader;
}): Promise<AtcStation> => {
  const requestedStationResult = await loadStation(requestedStation, forceRefresh);

  if (hasAvailableScenarios(requestedStationResult)) {
    return requestedStationResult;
  }

  Logger.warn('Selected station returned zero scenarios; searching for fallback station', LOG_TAG, {
    requestedStation: requestedStation.displayName,
    forceRefresh,
  });

  const fallbackCandidates = availableStations.filter(
    (candidate) => !isSameStation(candidate, requestedStation)
  );

  for (const candidate of fallbackCandidates) {
    try {
      const candidateResult = await loadStation(candidate, forceRefresh);

      if (hasAvailableScenarios(candidateResult)) {
        Logger.info('Falling back to first available ATC station', LOG_TAG, {
          requestedStation: requestedStation.displayName,
          fallbackStation: candidate.displayName,
          scenariosLoaded: candidateResult.scenarios.length,
        });
        return candidateResult;
      }

      Logger.warn('Fallback candidate also returned zero scenarios', LOG_TAG, {
        requestedStation: requestedStation.displayName,
        fallbackCandidate: candidate.displayName,
      });
    } catch (error) {
      Logger.warn('Fallback candidate failed to load; continuing search', LOG_TAG, {
        requestedStation: requestedStation.displayName,
        fallbackCandidate: candidate.displayName,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }

  throw new Error(buildNoFallbackStationMessage(requestedStation));
};

/**
 * Data source types for SWR tracking
 */
export type StationsDataSource = 'initial' | 'cache' | 'network' | 'background';

/**
 * Fetch stations options for SWR pattern
 */
export interface FetchStationsOptions {
  /** Force refresh from network even if cache exists */
  forceRefresh?: boolean;
  /** Whether this is a background refresh (merge-safe update) */
  background?: boolean;
}

/**
 * Recovery state for a single station
 * Tracks 404 recovery attempts and broken links status
 */
export interface StationRecoveryState {
  refreshAttempts: number;
  brokenLinks: boolean;
  hasShownBrokenLinksToast: boolean;
  lastFailureTime: number | null;
}

/**
 * Recovery state map keyed by stationKey (format: "airportCode:stationType")
 */
export type RecoveryStateMap = {
  [stationKey: string]: StationRecoveryState;
};

/**
 * ATC State interface
 */
export interface AtcState {
  // Station selection
  stations: AtcStation[];
  stationsLoading: boolean;
  stationsError: string | null;
  /** Tracks where station data came from (for SWR debugging) */
  stationsDataSource: StationsDataSource;
  /** Tracks if a background refresh is in progress */
  stationsBackgroundRefreshing: boolean;

  // Selected station
  selectedStation: AtcStation | null;
  selectedStationLoading: boolean;
  selectedStationError: string | null;

  // Current scenario playback
  currentScenario: AtcScenario | null;
  currentScenarioIndex: number;

  // Playback state
  isPlaying: boolean;
  isLoading: boolean;
  progress: number; // 0-100
  currentTime: number; // seconds
  duration: number; // seconds

  // History
  playedScenarioIds: string[];

  // Recovery state for 404 handling (keyed by stationKey)
  recoveryState: RecoveryStateMap;
}

const initialState: AtcState = {
  stations: [],
  stationsLoading: false,
  stationsError: null,
  stationsDataSource: 'initial',
  stationsBackgroundRefreshing: false,

  selectedStation: null,
  selectedStationLoading: false,
  selectedStationError: null,

  currentScenario: null,
  currentScenarioIndex: -1,

  isPlaying: false,
  isLoading: false,
  progress: 0,
  currentTime: 0,
  duration: 0,

  playedScenarioIds: [],

  recoveryState: {},
};

/**
 * Async thunk to fetch available stations from the main manifest
 * 
 * Supports SWR pattern:
 * - When `background: true`, dispatches merge-safe update that preserves playback state
 * - Returns early if a background refresh is already in progress
 *
 * Strangler Pattern:
 * When useCleanStationFetch flag is enabled, delegates to clean architecture thunk.
 */
export const fetchStations = createAsyncThunk(
  'atc/fetchStations',
  async (
    options: FetchStationsOptions | boolean = false,
    { rejectWithValue, extra, getState, dispatch }
  ) => {
    // Normalize options (backward compatibility: boolean = forceRefresh)
    const opts: FetchStationsOptions = typeof options === 'boolean'
      ? { forceRefresh: options, background: false }
      : options;

    // Strangler pattern: check feature flag for clean architecture path
    if (isCleanStationFetchEnabled()) {
      Logger.info('Using clean architecture path for stations fetch', LOG_TAG);
      const result = await dispatch(cleanFetchStations(opts)).unwrap();
      return result;
    }

    // Legacy implementation below
    const { forceRefresh = false, background = false } = opts;

    // Check if background refresh is already in progress
    const state = getState() as RootState;
    if (background && state.atc.stationsBackgroundRefreshing) {
      Logger.debug('Background refresh already in progress, skipping', LOG_TAG);
      return { stations: state.atc.stations, isBackground: true, skipped: true };
    }

    try {
      Logger.info(`Fetching ATC stations (forceRefresh=${forceRefresh}, background=${background})`, LOG_TAG);
      const manifestService = resolveAtcManifestService(extra);
      const stations = await manifestService.getAvailableStations(forceRefresh);
      Logger.info(`Loaded ${stations.length} stations (background=${background})`, LOG_TAG);

      // Mark ATC stations ready for performance tracking (only on initial load)
      if (!background) {
        getPerformanceTimingService().mark(PERFORMANCE_MARKS.ATC_STATIONS_READY);
      }

      return { stations, isBackground: background, skipped: false };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Failed to fetch stations: ${message}`, LOG_TAG);

      // Route error through centralized error pipeline
      ErrorHandlerService.getInstance().handleDataFetchError(error, 'atc-cdn', {
        operation: 'fetchStations',
        metadata: { forceRefresh, background },
      });

      return rejectWithValue(message);
    }
  }
);

/**
 * Async thunk to select a station and load its scenarios
 *
 * Strangler Pattern:
 * When useCleanStationFetch flag is enabled, delegates to clean architecture thunk.
 */
export const selectStation = createAsyncThunk(
  'atc/selectStation',
  async (station: AtcStation, { rejectWithValue, extra, getState }) => {
    try {
      Logger.info(`Selecting station: ${station.displayName}`, LOG_TAG);
      const state = getState() as RootState;
      const loadStation: StationLoader = isCleanStationFetchEnabled()
        ? (candidate, forceRefresh) => loadStationScenariosWithCleanArchitecture(candidate, forceRefresh)
        : (() => {
          const manifestService = resolveAtcManifestService(extra);
          return (candidate: AtcStation, forceRefresh: boolean) =>
            manifestService.loadStationScenarios(candidate, forceRefresh);
        })();

      const stationWithScenarios = await loadStationWithFallback({
        requestedStation: station,
        availableStations: state.atc.stations,
        forceRefresh: false,
        loadStation,
      });

      Logger.info(`Loaded ${stationWithScenarios.scenarios?.length ?? 0} scenarios for ${station.displayName}`, LOG_TAG);
      return stationWithScenarios;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Failed to load station scenarios: ${message}`, LOG_TAG);

      // Route error through centralized error pipeline
      ErrorHandlerService.getInstance().handleDataFetchError(error, 'atc-cdn', {
        operation: 'selectStation',
        metadata: { stationCode: station.airportCode, stationType: station.stationType },
      });

      return rejectWithValue(message);
    }
  }
);

/**
 * Async thunk to force-refresh a station's scenarios (bypass station manifest cache).
 * Used as a recovery mechanism when an ATC scenario URL 404s.
 *
 * Strangler Pattern:
 * When useCleanStationFetch flag is enabled, delegates to clean architecture thunk.
 */
export const refreshStationScenarios = createAsyncThunk(
  'atc/refreshStationScenarios',
  async (station: AtcStation, { rejectWithValue, extra, getState }) => {
    try {
      Logger.info(`Refreshing station scenarios (force): ${station.displayName}`, LOG_TAG);
      const state = getState() as RootState;
      const loadStation: StationLoader = isCleanStationFetchEnabled()
        ? (candidate, forceRefresh) => loadStationScenariosWithCleanArchitecture(candidate, forceRefresh)
        : (() => {
          const manifestService = resolveAtcManifestService(extra);
          return (candidate: AtcStation, forceRefresh: boolean) =>
            manifestService.loadStationScenarios(candidate, forceRefresh);
        })();

      const stationWithScenarios = await loadStationWithFallback({
        requestedStation: station,
        availableStations: state.atc.stations,
        forceRefresh: true,
        loadStation,
      });

      Logger.info(
        `Refreshed ${stationWithScenarios.scenarios?.length ?? 0} scenarios for ${station.displayName}`,
        LOG_TAG
      );
      return stationWithScenarios;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Failed to refresh station scenarios: ${message}`, LOG_TAG);

      // Route error through centralized error pipeline
      ErrorHandlerService.getInstance().handleDataFetchError(error, 'atc-cdn', {
        operation: 'refreshStationScenarios',
        metadata: { stationCode: station.airportCode, stationType: station.stationType },
      });

      return rejectWithValue(message);
    }
  }
);

/**
 * Async thunk to auto-select the first available station
 */
export const autoSelectFirstStation = createAsyncThunk(
  'atc/autoSelectFirstStation',
  async (_: void, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;

      if (state.atc.selectedStation) {
        Logger.info('Auto-select skipped; station already selected', LOG_TAG);
        return state.atc.selectedStation;
      }

      const stations = state.atc.stations;

      if (!stations || stations.length === 0) {
        Logger.warn('Auto-select skipped; no stations available', LOG_TAG);
        return null;
      }

      const firstStation = stations[0];
      const stationWithScenarios = await dispatch(selectStation(firstStation)).unwrap();
      return stationWithScenarios;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Failed to auto-select station: ${message}`, LOG_TAG);

      // Route error through centralized error pipeline
      ErrorHandlerService.getInstance().handleDataFetchError(error, 'atc-cdn', {
        operation: 'autoSelectFirstStation',
      });

      return rejectWithValue(message);
    }
  }
);

/**
 * Async thunk to select station by airport code and station type
 */
export const selectStationByCode = createAsyncThunk(
  'atc/selectStationByCode',
  async ({ airportCode, stationType }: { airportCode: string; stationType: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      let stations = state.atc.stations;

      // If stations not loaded, fetch them first
      if (stations.length === 0) {
        const result = await dispatch(fetchStations({ forceRefresh: false })).unwrap();
        stations = result.stations;
      }

      // Find the matching station
      const station = stations.find(
        s => s.airportCode.toLowerCase() === airportCode.toLowerCase() &&
          s.stationType.toLowerCase() === stationType.toLowerCase()
      );

      if (!station) {
        throw new Error(`Station not found: ${airportCode} ${stationType}`);
      }

      // Select the station (this will load scenarios)
      const stationWithScenarios = await dispatch(selectStation(station)).unwrap();
      return stationWithScenarios;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Failed to select station by code: ${message}`, LOG_TAG);

      // Route error through centralized error pipeline
      ErrorHandlerService.getInstance().handleDataFetchError(error, 'atc-cdn', {
        operation: 'selectStationByCode',
        metadata: { airportCode, stationType },
      });

      return rejectWithValue(message);
    }
  }
);

/**
 * ATC Slice
 */
export const atcSlice = createSlice({
  name: 'atc',
  initialState,
  reducers: {
    // Clear selected station
    clearSelectedStation(state) {
      state.selectedStation = null;
      state.currentScenario = null;
      state.currentScenarioIndex = -1;
      state.progress = 0;
      state.currentTime = 0;
      state.duration = 0;
    },

    // Set current scenario by index
    setCurrentScenarioByIndex(state, action: PayloadAction<number>) {
      const index = action.payload;
      if (state.selectedStation?.scenarios && index >= 0 && index < state.selectedStation.scenarios.length) {
        state.currentScenarioIndex = index;
        state.currentScenario = state.selectedStation.scenarios[index];
        state.progress = 0;
        state.currentTime = 0;
        state.isLoading = true;
      }
    },

    // Move to next scenario
    nextScenario(state) {
      if (!state.selectedStation?.scenarios || state.selectedStation.scenarios.length === 0) {
        return;
      }

      // Ensure playedScenarioIds is initialized (handles persisted state migration)
      if (!state.playedScenarioIds) {
        state.playedScenarioIds = [];
      }

      // Add current scenario to played list
      if (state.currentScenario?.scenario_id) {
        if (!state.playedScenarioIds.includes(state.currentScenario.scenario_id)) {
          state.playedScenarioIds.push(state.currentScenario.scenario_id);
        }
      }

      const nextIndex = (state.currentScenarioIndex + 1) % state.selectedStation.scenarios.length;
      state.currentScenarioIndex = nextIndex;
      state.currentScenario = state.selectedStation.scenarios[nextIndex];
      state.progress = 0;
      state.currentTime = 0;
      state.isLoading = true;

      Logger.info(`Moving to next scenario: ${state.currentScenario.scenario_id}`, LOG_TAG);
    },

    // Move to random scenario (not in recently played)
    randomScenario(state) {
      if (!state.selectedStation?.scenarios || state.selectedStation.scenarios.length === 0) {
        return;
      }

      // Ensure playedScenarioIds is initialized (handles persisted state migration)
      if (!state.playedScenarioIds) {
        state.playedScenarioIds = [];
      }

      const scenarios = state.selectedStation.scenarios;

      // Find scenarios not recently played
      const unplayedScenarios = scenarios.filter(
        s => !state.playedScenarioIds.includes(s.scenario_id)
      );

      // If all played, reset history and pick random
      const pool = unplayedScenarios.length > 0 ? unplayedScenarios : scenarios;

      if (pool.length === scenarios.length && state.playedScenarioIds.length > 0) {
        // Reset played list if all scenarios have been played
        state.playedScenarioIds = [];
      }

      const randomIndex = Math.floor(Math.random() * pool.length);
      const randomScenario = pool[randomIndex];
      const actualIndex = scenarios.findIndex(s => s.scenario_id === randomScenario.scenario_id);

      state.currentScenarioIndex = actualIndex;
      state.currentScenario = randomScenario;
      state.progress = 0;
      state.currentTime = 0;
      state.isLoading = true;

      Logger.info(`Selected random scenario: ${randomScenario.scenario_id}`, LOG_TAG);
    },

    // Update playback progress
    updateProgress(state, action: PayloadAction<{ currentTime: number; duration: number }>) {
      const { currentTime, duration } = action.payload;
      state.currentTime = currentTime;
      state.duration = duration;
      state.progress = duration > 0 ? (currentTime / duration) * 100 : 0;
    },

    // Set playing state
    setIsPlaying(state, action: PayloadAction<boolean>) {
      state.isPlaying = action.payload;
    },

    // Set loading state
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    // Clear playback state
    clearPlayback(state) {
      state.currentScenario = null;
      state.currentScenarioIndex = -1;
      state.progress = 0;
      state.currentTime = 0;
      state.duration = 0;
      state.isPlaying = false;
      state.isLoading = false;
    },

    // Reset played history
    resetPlayedHistory(state) {
      state.playedScenarioIds = [];
    },

    // SWR: Update stations from background refresh (merge-safe)
    // Preserves: selectedStation, currentScenario, playback state
    updateStationsFromBackground(state, action: PayloadAction<AtcStation[]>) {
      const newStations = action.payload;
      const previousSelectedStation = state.selectedStation;

      // Update stations list
      state.stations = newStations;
      state.stationsDataSource = 'background';
      state.stationsBackgroundRefreshing = false;

      // Preserve selected station if it still exists
      if (previousSelectedStation) {
        const matchedStation = findMatchingStation(newStations, previousSelectedStation);
        if (matchedStation) {
          // Merge: keep scenarios from previous state if available
          state.selectedStation = mergeStationScenarios(matchedStation, previousSelectedStation);
          Logger.debug('Background update: preserved selected station', LOG_TAG);
        } else {
          Logger.warn('Background update: selected station no longer in manifest', LOG_TAG);
          // Don't clear selection - user might still be playing
          // The station selector UI can show a warning if needed
        }
      }

      // Note: currentScenario, isPlaying, progress etc. are NOT touched
      // This ensures playback continues uninterrupted
      Logger.info(`Background stations update applied: ${newStations.length} stations`, LOG_TAG);
    },

    // SWR: Set background refreshing status
    setStationsBackgroundRefreshing(state, action: PayloadAction<boolean>) {
      state.stationsBackgroundRefreshing = action.payload;
    },

    // ========================================================================
    // Recovery State Reducers
    // Manage 404 recovery state per station (explicit Redux state)
    // ========================================================================

    /**
     * Record a scenario failure (404) for a station.
     * Increments refresh attempts and records failure time.
     */
    recordScenarioFailure(state, action: PayloadAction<string>) {
      const stationKey = action.payload;
      const existing = state.recoveryState[stationKey];

      state.recoveryState[stationKey] = {
        refreshAttempts: (existing?.refreshAttempts ?? 0) + 1,
        brokenLinks: existing?.brokenLinks ?? false,
        hasShownBrokenLinksToast: existing?.hasShownBrokenLinksToast ?? false,
        lastFailureTime: Date.now(),
      };
    },

    /**
     * Mark a station as having broken links (404s persist after manifest refresh).
     */
    markStationBrokenLinks(state, action: PayloadAction<string>) {
      const stationKey = action.payload;
      const existing = state.recoveryState[stationKey];

      state.recoveryState[stationKey] = {
        refreshAttempts: existing?.refreshAttempts ?? 0,
        brokenLinks: true,
        hasShownBrokenLinksToast: existing?.hasShownBrokenLinksToast ?? false,
        lastFailureTime: existing?.lastFailureTime ?? null,
      };
    },

    /**
     * Mark that the broken links toast has been shown for a station.
     * Prevents showing the toast multiple times.
     */
    markBrokenLinksToastShown(state, action: PayloadAction<string>) {
      const stationKey = action.payload;
      const existing = state.recoveryState[stationKey];

      state.recoveryState[stationKey] = {
        refreshAttempts: existing?.refreshAttempts ?? 0,
        brokenLinks: existing?.brokenLinks ?? false,
        hasShownBrokenLinksToast: true,
        lastFailureTime: existing?.lastFailureTime ?? null,
      };
    },

    /**
     * Reset recovery state for a station (e.g., after successful scenario load).
     */
    resetStationRecoveryState(state, action: PayloadAction<string>) {
      const stationKey = action.payload;
      // Remove from map entirely to keep state clean
      delete state.recoveryState[stationKey];
    },
  },
  extraReducers: (builder) => {
    // Fetch stations
    builder
      .addCase(fetchStations.pending, (state, action) => {
        const opts = typeof action.meta.arg === 'boolean'
          ? { forceRefresh: action.meta.arg, background: false }
          : (action.meta.arg || { forceRefresh: false, background: false });

        if (opts.background) {
          // Background refresh: don't show loading state
          state.stationsBackgroundRefreshing = true;
        } else {
          state.stationsLoading = true;
        }
        state.stationsError = null;
      })
      .addCase(fetchStations.fulfilled, (state, action) => {
        const { stations, isBackground, skipped } = action.payload;

        // If skipped (duplicate background request), do nothing
        if (skipped) {
          return;
        }

        if (isBackground) {
          // Background refresh: merge-safe update
          state.stationsBackgroundRefreshing = false;
          state.stationsDataSource = 'background';

          // Update stations list
          state.stations = stations;

          // Preserve selected station if it still exists
          if (state.selectedStation) {
            const matchedStation = findMatchingStation(stations, state.selectedStation);
            if (matchedStation) {
              state.selectedStation = mergeStationScenarios(matchedStation, state.selectedStation);
              Logger.debug('Background fetch: preserved selected station', LOG_TAG);
            }
            // Don't clear selection if station removed - user might still be playing
          }

          Logger.info(`Background stations fetch applied: ${stations.length} stations`, LOG_TAG);
        } else {
          // Initial/foreground fetch: full state update
          state.stationsLoading = false;
          state.stations = stations;
          state.stationsError = null;
          state.stationsDataSource = 'network';

          const matchedStation = findMatchingStation(stations, state.selectedStation);

          if (state.selectedStation && !matchedStation) {
            Logger.warn('Persisted station no longer exists in manifest; clearing selection', LOG_TAG);
            state.selectedStation = null;
            state.selectedStationLoading = false;
            state.selectedStationError = null;
            state.currentScenario = null;
            state.currentScenarioIndex = -1;
            state.progress = 0;
            state.currentTime = 0;
            state.duration = 0;
          } else if (matchedStation && state.selectedStation) {
            state.selectedStation = mergeStationScenarios(matchedStation, state.selectedStation);
          }
        }
      })
      .addCase(fetchStations.rejected, (state, action) => {
        const opts = typeof action.meta.arg === 'boolean'
          ? { forceRefresh: action.meta.arg, background: false }
          : (action.meta.arg || { forceRefresh: false, background: false });

        if (opts.background) {
          // Background refresh failed: just log, don't show error
          state.stationsBackgroundRefreshing = false;
          Logger.warn(`Background stations fetch failed: ${action.payload}`, LOG_TAG);
        } else {
          state.stationsLoading = false;
          state.stationsError = action.payload as string;
        }
      });

    // Select station
    builder
      .addCase(selectStation.pending, (state) => {
        state.selectedStationLoading = true;
        state.selectedStationError = null;
        state.isLoading = true;
      })
      .addCase(selectStation.fulfilled, (state, action) => {
        state.selectedStationLoading = false;
        applyLoadedStationSelection(state, action.payload);
      })
      .addCase(selectStation.rejected, (state, action) => {
        state.selectedStationLoading = false;
        state.selectedStationError = action.payload as string;
        state.isLoading = false;
      });

    // Refresh station scenarios (force manifest refresh)
    builder
      .addCase(refreshStationScenarios.pending, (state) => {
        state.selectedStationLoading = true;
        state.selectedStationError = null;
        state.isLoading = true;
      })
      .addCase(refreshStationScenarios.fulfilled, (state, action) => {
        state.selectedStationLoading = false;
        applyRefreshedStationSelection(state, action.payload);
      })
      .addCase(refreshStationScenarios.rejected, (state, action) => {
        state.selectedStationLoading = false;
        state.selectedStationError = action.payload as string;
        state.isLoading = false;
      });

    // Select station by code
    builder
      .addCase(selectStationByCode.pending, (state) => {
        state.selectedStationLoading = true;
        state.selectedStationError = null;
        state.isLoading = true;
      })
      .addCase(selectStationByCode.rejected, (state, action) => {
        state.selectedStationLoading = false;
        state.selectedStationError = action.payload as string;
        state.isLoading = false;
      });

    // ========================================================================
    // Clean Architecture Thunks (Strangler Pattern)
    // These handle the same state transitions as legacy thunks above
    // ========================================================================

    // Clean fetch stations
    builder
      .addCase(cleanFetchStations.pending, (state, action) => {
        const opts = typeof action.meta.arg === 'boolean'
          ? { forceRefresh: action.meta.arg, background: false }
          : (action.meta.arg || { forceRefresh: false, background: false });

        if (opts.background) {
          state.stationsBackgroundRefreshing = true;
        } else {
          state.stationsLoading = true;
        }
        state.stationsError = null;
      })
      .addCase(cleanFetchStations.fulfilled, (state, action) => {
        const { stations, isBackground, skipped } = action.payload;

        if (skipped) {
          return;
        }

        if (isBackground) {
          state.stationsBackgroundRefreshing = false;
          state.stationsDataSource = 'background';
          state.stations = stations;

          if (state.selectedStation) {
            const matchedStation = findMatchingStation(stations, state.selectedStation);
            if (matchedStation) {
              state.selectedStation = mergeStationScenarios(matchedStation, state.selectedStation);
            }
          }
        } else {
          state.stationsLoading = false;
          state.stations = stations;
          state.stationsError = null;
          state.stationsDataSource = 'network';

          const matchedStation = findMatchingStation(stations, state.selectedStation);

          if (state.selectedStation && !matchedStation) {
            state.selectedStation = null;
            state.selectedStationLoading = false;
            state.selectedStationError = null;
            state.currentScenario = null;
            state.currentScenarioIndex = -1;
            state.progress = 0;
            state.currentTime = 0;
            state.duration = 0;
          } else if (matchedStation && state.selectedStation) {
            state.selectedStation = mergeStationScenarios(matchedStation, state.selectedStation);
          }
        }
      })
      .addCase(cleanFetchStations.rejected, (state, action) => {
        const opts = typeof action.meta.arg === 'boolean'
          ? { forceRefresh: action.meta.arg, background: false }
          : (action.meta.arg || { forceRefresh: false, background: false });

        if (opts.background) {
          state.stationsBackgroundRefreshing = false;
        } else {
          state.stationsLoading = false;
          state.stationsError = action.payload as string;
        }
      });

    // Clean select station
    builder
      .addCase(cleanSelectStation.pending, (state) => {
        state.selectedStationLoading = true;
        state.selectedStationError = null;
        state.isLoading = true;
      })
      .addCase(cleanSelectStation.fulfilled, (state, action) => {
        state.selectedStationLoading = false;
        applyLoadedStationSelection(state, action.payload);
      })
      .addCase(cleanSelectStation.rejected, (state, action) => {
        state.selectedStationLoading = false;
        state.selectedStationError = action.payload as string;
        state.isLoading = false;
      });

    // Clean refresh station scenarios
    builder
      .addCase(cleanRefreshStationScenarios.pending, (state) => {
        state.selectedStationLoading = true;
        state.selectedStationError = null;
        state.isLoading = true;
      })
      .addCase(cleanRefreshStationScenarios.fulfilled, (state, action) => {
        state.selectedStationLoading = false;
        applyRefreshedStationSelection(state, action.payload);
      })
      .addCase(cleanRefreshStationScenarios.rejected, (state, action) => {
        state.selectedStationLoading = false;
        state.selectedStationError = action.payload as string;
        state.isLoading = false;
      });
  },
});

// Export actions
export const {
  clearSelectedStation,
  setCurrentScenarioByIndex,
  nextScenario,
  randomScenario,
  updateProgress,
  setIsPlaying,
  setIsLoading,
  clearPlayback,
  resetPlayedHistory,
  updateStationsFromBackground,
  setStationsBackgroundRefreshing,
  // Recovery actions
  recordScenarioFailure,
  markStationBrokenLinks,
  markBrokenLinksToastShown,
  resetStationRecoveryState,
} = atcSlice.actions;

const EMPTY_STATIONS: AtcStation[] = [];
let hasLoggedInvalidStationsState = false;

const getSafeStations = (state: RootState): AtcStation[] => {
  const atcState = state.atc as Partial<AtcState> | undefined;
  const stations = atcState?.stations;

  if (Array.isArray(stations)) {
    return stations;
  }

  if (!hasLoggedInvalidStationsState) {
    hasLoggedInvalidStationsState = true;
    Logger.error('ATC stations state was not an array; defaulting to an empty list', LOG_TAG, {
      hasAtcState: !!atcState,
      stationsType: typeof stations,
      atcKeys: atcState ? Object.keys(atcState) : [],
    });
  }

  return EMPTY_STATIONS;
};

// Selectors
export const selectStations = (state: RootState) => getSafeStations(state);
export const selectStationsLoading = (state: RootState) => state.atc.stationsLoading;
export const selectStationsError = (state: RootState) => state.atc.stationsError;

export const selectSelectedStation = (state: RootState) => state.atc.selectedStation;
export const selectSelectedStationLoading = (state: RootState) => state.atc.selectedStationLoading;
export const selectSelectedStationError = (state: RootState) => state.atc.selectedStationError;

export const selectCurrentScenario = (state: RootState) => state.atc.currentScenario;
export const selectCurrentScenarioIndex = (state: RootState) => state.atc.currentScenarioIndex;

export const selectAtcIsPlaying = (state: RootState) => state.atc.isPlaying;
export const selectAtcIsLoading = (state: RootState) => state.atc.isLoading;
export const selectAtcProgress = (state: RootState) => state.atc.progress;
export const selectAtcCurrentTime = (state: RootState) => state.atc.currentTime;
export const selectAtcDuration = (state: RootState) => state.atc.duration;

export const selectPlayedScenarioIds = (state: RootState) => state.atc.playedScenarioIds ?? [];

// SWR selectors
export const selectStationsDataSource = (state: RootState) => state.atc.stationsDataSource;
export const selectStationsBackgroundRefreshing = (state: RootState) => state.atc.stationsBackgroundRefreshing;

// Recovery state selectors
export const selectRecoveryStateMap = (state: RootState) => state.atc.recoveryState;

/**
 * Select recovery state for a specific station.
 * Returns undefined if no recovery state exists for the station.
 */
export const selectStationRecoveryState = (state: RootState, stationKey: string): StationRecoveryState | undefined =>
  state.atc.recoveryState[stationKey];

/**
 * Check if a station has broken links (404s persist after manifest refresh).
 */
export const selectHasStationBrokenLinks = (state: RootState, stationKey: string): boolean =>
  state.atc.recoveryState[stationKey]?.brokenLinks ?? false;

/**
 * Check if the broken links toast has been shown for a station.
 */
export const selectHasShownBrokenLinksToast = (state: RootState, stationKey: string): boolean =>
  state.atc.recoveryState[stationKey]?.hasShownBrokenLinksToast ?? false;

/**
 * Get the refresh attempts count for a station.
 */
export const selectStationRefreshAttempts = (state: RootState, stationKey: string): number =>
  state.atc.recoveryState[stationKey]?.refreshAttempts ?? 0;

// Computed selectors
export const selectCurrentScenarioUrl = (state: RootState) =>
  state.atc.currentScenario?.cdn_url ?? null;

export const selectStationDisplayName = (state: RootState) =>
  state.atc.selectedStation ? getStationLabelWithoutType(state.atc.selectedStation) : '';

export const selectScenarioCount = (state: RootState) =>
  Array.isArray(state.atc.selectedStation?.scenarios)
    ? state.atc.selectedStation.scenarios.length
    : 0;

export default atcSlice.reducer;
